import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, Sparkles, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REGIONS } from "@/lib/regions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  submitter_name: "",
  submitter_email: "",
  name: "",
  description: "",
  category: "coop",
  region: "central",
  url: "",
  contact: "",
  teaching_philosophy: "",
  florida_expertise: "",
};

export default function SuggestResource() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const doneRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.submitter_name.trim().length < 2) return toast.error("Add your name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.submitter_email.trim())) return toast.error("Enter a valid email.");
    if (form.name.trim().length < 2) return toast.error("Give the resource a name.");
    if (form.description.trim().length < 10) return toast.error("Add a few more words to the description.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v && String(v).trim()) fd.append(k, String(v).trim());
      });
      const res = await fetch(`${API}/resource-suggestions/public`, { method: "POST", body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Submission failed (${res.status})`);
      }
      setDone(true);
      setTimeout(() => doneRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    } catch (err) {
      toast.error(err.message || "Submit failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/40 via-white to-white">
      <Seo
        title="Suggest a Florida Homeschool Resource · Homeschool Hub"
        description="Know a great Florida co-op or tutor? Submit them to our directory. Free listing after admin review."
        canonical="/suggest-a-resource"
      />

      <header className="mx-auto max-w-3xl px-6 pt-10 pb-4">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-amber-600" data-testid="back-to-landing">
          <ArrowLeft className="w-4 h-4" /> Back to homepage
        </Link>
      </header>

      <main className="mx-auto max-w-3xl px-6 pb-24">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-amber-600">Community directory</div>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Know a Florida co-op or tutor we should list?
        </h1>
        <p className="mt-3 text-stone-600 max-w-xl">
          Submit them below and our team reviews every entry before it goes live in the directory. Free listing — no login required.
        </p>

        {done ? (
          <div
            ref={doneRef}
            className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center"
            data-testid="suggest-thanks"
          >
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <h2 className="mt-4 font-display text-2xl font-semibold">Thanks, {form.submitter_name.split(" ")[0] || "friend"}!</h2>
            <p className="mt-2 text-stone-600 max-w-md mx-auto">
              We've queued <strong>{form.name}</strong> for review. If it's a fit, you'll see it live in the directory within a few days. We may email you at <strong>{form.submitter_email}</strong> if we need any clarifying details.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => { setForm(EMPTY); setDone(false); }}
                className="rounded-full"
                data-testid="suggest-another"
              >
                Suggest another
              </Button>
              <Link to="/">
                <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white">
                  Back to homepage
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-8" data-testid="suggest-public-form">
            {/* Your info */}
            <section className="rounded-2xl border border-stone-200 bg-white p-6 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-600">Step 1</div>
                <h3 className="mt-1 font-semibold text-lg">Who's suggesting?</h3>
                <p className="text-sm text-stone-500">So we can follow up if we need clarification. Not shown publicly.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="s-name">Your name</Label>
                  <Input id="s-name" value={form.submitter_name} onChange={set("submitter_name")} className="mt-1.5" data-testid="submitter-name" placeholder="Jane Homeschooler" />
                </div>
                <div>
                  <Label htmlFor="s-email">Your email</Label>
                  <Input id="s-email" type="email" value={form.submitter_email} onChange={set("submitter_email")} className="mt-1.5" data-testid="submitter-email" placeholder="jane@example.com" />
                </div>
              </div>
            </section>

            {/* Resource details */}
            <section className="rounded-2xl border border-stone-200 bg-white p-6 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-600">Step 2</div>
                <h3 className="mt-1 font-semibold text-lg">About the resource</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="suggest-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="coop">Co-op</SelectItem>
                      <SelectItem value="tutor">Tutor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Region</Label>
                  <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="suggest-region"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REGIONS.map((r) => (
                        <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="r-name">Name of the co-op or tutor</Label>
                <Input id="r-name" value={form.name} onChange={set("name")} placeholder="e.g. Sunshine Homeschool Co-op" className="mt-1.5" data-testid="resource-name" />
              </div>
              <div>
                <Label htmlFor="r-desc">What they offer</Label>
                <Textarea id="r-desc" value={form.description} onChange={set("description")} placeholder="What subjects, ages, meeting cadence, cost, teaching approach?" rows={4} className="mt-1.5 resize-none" data-testid="resource-description" />
                <div className="mt-1 text-[11px] text-stone-400 text-right">{form.description.length}/2000</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="r-url">Website <span className="text-stone-400 font-normal text-xs">(optional)</span></Label>
                  <Input id="r-url" value={form.url} onChange={set("url")} placeholder="https://…" className="mt-1.5" data-testid="resource-url" />
                </div>
                <div>
                  <Label htmlFor="r-contact">Contact <span className="text-stone-400 font-normal text-xs">(optional)</span></Label>
                  <Input id="r-contact" value={form.contact} onChange={set("contact")} placeholder="Email or phone" className="mt-1.5" data-testid="resource-contact" />
                </div>
              </div>
            </section>

            {/* Optional bio */}
            <section className="rounded-2xl border border-stone-200 bg-white p-6 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-600">Optional</div>
                <h3 className="mt-1 font-semibold text-lg">Teaching philosophy & Florida expertise</h3>
                <p className="text-sm text-stone-500">Only used if this is your own listing. Helps families understand your approach — shown publicly if approved.</p>
              </div>
              <div>
                <Label htmlFor="r-phil">Teaching philosophy</Label>
                <Textarea id="r-phil" value={form.teaching_philosophy} onChange={set("teaching_philosophy")} placeholder="e.g. Interest-led, Charlotte-Mason inspired. I believe kids learn best when…" rows={3} className="mt-1.5 resize-none" data-testid="resource-philosophy" />
              </div>
              <div>
                <Label htmlFor="r-fl">Florida-specific expertise</Label>
                <Textarea id="r-fl" value={form.florida_expertise} onChange={set("florida_expertise")} placeholder="e.g. FES-EO reimbursement, WRAT-5 prep, FL Statute 1002.41 compliance…" rows={3} className="mt-1.5 resize-none" data-testid="resource-fl-expertise" />
              </div>
            </section>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-stone-500 max-w-xs">
                By submitting you agree we may contact you at the email above for verification. We never share your details.
              </p>
              <Button type="submit" disabled={submitting} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-6 py-6 h-auto" data-testid="suggest-submit-public">
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-2" /> Submit for review</>)}
              </Button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
